import React, { useState, useEffect, useCallback } from 'react';
import { Grid2, Paper, Modal, Button as MuiButton } from '@mui/material';
import DragAndDropInput from '../components/draganddropinput';
import axios from 'axios';
import './databasepage.css';

// Import Ant Design components
import { Steps, Spin, Button as AntButton, Space, ConfigProvider, message } from 'antd';
import esES from 'antd/locale/es_ES';

// Import icons
import { RiFileUploadFill } from 'react-icons/ri';
import { FileAddOutlined, ReloadOutlined } from '@ant-design/icons';

//BACK END URL
import { NODE_BACKEND_API_URL } from '../config';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Step } = Steps;

function RepoPage() {
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // STATUS AND REPORT STATES - Managed here now
    const [uploadStatus, setUploadStatus] = useState('');
    const [pythonStatus, setPythonStatus] = useState('');
    const [databaseStatus, setDatabaseStatus] = useState('');
    const [processStatus, setProcessStatus] = useState(''); // Overall process status
    const [reportMetrics, setReportMetrics] = useState(null);

    // NEW STATE TO TRACK THE SELECTED FILE IN REPOPAGE
    const [selectedFile, setSelectedFile] = useState(null);


    // --- NEW STATE FOR ANT DESIGN STEPS ---
    const [currentStep, setCurrentStep] = useState(0); // Start at step 0 (Initial State)

    // Define the steps for the process with dynamic descriptions
    const fileProcessSteps = [
        {
            title: 'Inicio',
            description: 'Esperando archivo',
            status: currentStep === 0 ? 'process' : (currentStep > 0 && !processStatus.includes('Error') ? 'finish' : 'wait'),
        },
        {
            title: 'Archivo Seleccionado',
            description: selectedFile ? selectedFile.name : 'Listo para cargar', // Show selected file name
            status: currentStep === 1 ? 'process' : (currentStep > 1 && !processStatus.includes('Error') ? 'finish' : 'wait'),
        },
        {
            title: 'Carga',
            description: uploadStatus || 'Subiendo archivo', // Dynamic description based on uploadStatus
            status: currentStep === 2 ? (uploadStatus.includes('Error') ? 'error' : 'process') : (currentStep > 2 && !processStatus.includes('Error') ? 'finish' : 'wait'),
        },
        {
            title: 'Procesamiento',
            description: pythonStatus || 'Análisis Inteligente', // Dynamic description based on pythonStatus
            status: currentStep === 3 ? (pythonStatus.includes('Error') ? 'error' : 'process') : (currentStep > 3 && !processStatus.includes('Error') ? 'finish' : 'wait'),
        },
        {
            title: 'Actualización',
            description: databaseStatus || 'Base de datos', // Dynamic description based on databaseStatus
            status: currentStep === 4 ? (databaseStatus.includes('Error') ? 'error' : 'process') : (currentStep > 4 && !processStatus.includes('Error') ? 'finish' : 'wait'),
        },
        {
            title: 'Finalizado',
            description: processStatus || 'Proceso completado', // Dynamic description based on processStatus
            status: currentStep === 5 ? (processStatus.includes('Error') ? 'error' : 'finish') : 'wait',
        },
    ];

    const fetchFiles = useCallback(async () => {
        if (isDemoMode) {
            setFileList([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${NODE_BACKEND_API_URL}/repoview`);
            setFileList(response.data || []); // Ensure response.data is an array
            setLoading(false);
        } catch (err) {
            console.error("Error adquiriendo archivos:", err);
            setError(err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    // Handler for file selection in DragAndDropInput
    const handleFileSelected = useCallback((file) => {
        setSelectedFile(file); // Set the selected file in RepoPage state
        setCurrentStep(1); // Move to "Archivo Seleccionado" step
        resetStatusStates(); // Reset status states on new file selection
        setReportMetrics(null); // Clear previous report metrics
        setProcessStatus(''); // Clear overall process status
    }, []);

    // Handler for resetting the process to step 0
    const resetProcess = useCallback(() => {
        setCurrentStep(0);
        setSelectedFile(null); // Clear the selected file
        resetStatusStates(); // Reset all status states
        setReportMetrics(null); // Clear previous report metrics
        setProcessStatus(''); // Clear overall process status
    }, []);

    // Function to reset all status states
    const resetStatusStates = useCallback(() => {
        setUploadStatus('');
        setPythonStatus('');
        setDatabaseStatus('');
    }, []);

    // Function to extract filename without extension for report title
    const getReportTitle = useCallback(() => {
        if (!selectedFile) {
            return "Reporte: Actualización";
        }
        const filenameWithoutExtension = selectedFile.name.split('.')[0];
        return `Reporte: Actualización ${filenameWithoutExtension}`;
    }, [selectedFile]);


    // --- handleFileUpload in databasepage.js ---
    const handleFileUpload = useCallback(async (fileToUpload) => {
        console.log("handleFileUpload function called in RepoPage");
        if (!fileToUpload) {
            console.log("No file selected to upload in RepoPage");
            setCurrentStep(1);
            return;
        }
        console.log("handleFileUpload with file:", fileToUpload.name);

        resetStatusStates();
        setReportMetrics(null);
        setProcessStatus('');

        // Signal Step 2: Uploading (This now covers the whole backend process)
        setCurrentStep(2);
        setUploadStatus('Iniciando carga y procesamiento...'); // Unified status message

        const formData = new FormData();
        formData.append('file', fileToUpload);

        try {
            // SINGLE REQUEST to Node.js Backend which orchestrates Flask and DB Update
            // The endpoint remains the Node.js Multer endpoint: /apitest/droploader
            const finalResponse = await axios.post(`${NODE_BACKEND_API_URL}/droploader`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout:360000, // 6 minutes
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadStatus(`Cargando a backend... ${percentCompleted}%`);
                }
            });

            console.log("Respuesta final del proceso de carga y procesamiento:", finalResponse);

            // Assuming the final response from Node.js has status updates and metrics
            if (finalResponse.data && finalResponse.data.message) {
                // Update granular statuses based on backend response messages if available
                // Or set a single overall status message
                setUploadStatus('Archivo cargado y procesamiento iniciado en backend');
                setPythonStatus('Procesamiento con Python en curso...'); // Or get status from backend response
                setDatabaseStatus('Actualización de BD en curso...'); // Or get status from backend response
                setProcessStatus(finalResponse.data.message); // Overall success message

                // Assuming the final response contains report metrics directly or nested
                if (finalResponse.data.dbUpdateResponse && finalResponse.data.dbUpdateResponse.Reporte) {
                    setReportMetrics(finalResponse.data.dbUpdateResponse.Reporte);
                } else if (finalResponse.data.Reporte) { // Handle if Reporte is at top level
                    setReportMetrics(finalResponse.data.Reporte);
                } else {
                    setReportMetrics({ /* Default or empty metrics if not provided */ });
                }


                // Signal Step 5: Process Completed (or indicate success based on response)
                setCurrentStep(5);


            } else {
                console.error("Unexpected response format from Node.js backend:", finalResponse);
                setProcessStatus('Error: Formato de respuesta inesperado');
                setCurrentStep(5); // Move to final step with error indicated
            }


        } catch (error) {
            console.error("Error durante el proceso de carga y procesamiento:", error);

            // Update status messages based on the error
            if (error.response) {
                // Error response from Node.js backend
                console.error("Backend Error Data:", error.response.data);
                console.error("Backend Error Status:", error.response.status);
                setProcessStatus(`Error en backend: ${error.response.status} - ${error.response.data.message || error.message}`);
            } else if (error.request) {
                // Request made but no response received
                setProcessStatus(`Error de red: No se recibió respuesta`);
            } else {
                // Something else happened
                setProcessStatus(`Error: ${error.message}`);
            }

            setCurrentStep(5); // Move to final step with error status
        } finally {
            // Fetch files again after the process finishes (success or error)
            fetchFiles();
        }
    }, [fetchFiles, resetStatusStates, selectedFile, NODE_BACKEND_API_URL]); // Add NODE_BACKEND_API_URL dependency


    // Show initial loading for file list
    if (loading) {
        return (
            <div className="database-page-container">
                <div className="database-content-container" style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin tip="Cargando lista de archivos permitidos..." size="large" />
                </div>
            </div>
        );
    }

    // Show initial error if fetching file list failed
    if (error) {
        return (
            <div className="database-page-container">
                <div className="database-content-container" style={{ textAlign: 'center', padding: '50px', color: '#ff4d4f' }}>
                    <p>Error al cargar la lista de archivos permitidos:</p>
                    <p>{error.message}</p>
                    <AntButton type="primary" onClick={fetchFiles} style={{ marginTop: '20px' }}>
                        Reintentar
                    </AntButton>
                </div>
            </div>
        );
    }

    return (
        <ConfigProvider locale={esES}>
        <div className="database-page-container">
            {/* Header */}
            <div className="database-header">
                <h1>
                    <FileAddOutlined />
                    Carga de Nuevas Listas de Precios
                </h1>
                <AntButton
                    type="default"
                    icon={<ReloadOutlined />}
                    onClick={resetProcess}
                >
                    Reiniciar
                </AntButton>
            </div>

            {/* Steps */}
            <div className="database-steps-container">
                <Steps current={currentStep}>
                    {fileProcessSteps.map((item, index) => (
                        <Steps.Step 
                            key={index} 
                            title={item.title} 
                            description={item.description} 
                            status={item.status} 
                        />
                    ))}
                </Steps>
            </div>

            {/* Content */}
            <div className="database-content-container">


                {currentStep === 0 && (
                    <div className="database-drag-drop-zone">
                        <DragAndDropInput
                            allowedFileNames={isDemoMode ? null : (fileList ? fileList.map(fileInfo => fileInfo.FILE) : [])}
                            onFileSelected={handleFileSelected}
                        />
                    </div>
                )}

                {currentStep === 1 && selectedFile && (
                    <div className="database-file-info">
                        <h3>Información del Archivo Seleccionado</h3>
                        <p><strong>Nombre:</strong> {selectedFile.name}</p>
                        <p><strong>Última modificación:</strong> {selectedFile.lastModifiedDate ? new Date(selectedFile.lastModifiedDate).toLocaleString() : 'N/A'}</p>
                        <Space style={{ marginTop: '20px' }}>
                            <AntButton
                                type="primary"
                                size="large"
                                onClick={() => {
                                    if (isDemoMode) { message.info('Cargar lista — disponible en la versión completa del sistema'); return; }
                                    handleFileUpload(selectedFile);
                                }}
                            >
                                Cargar lista
                            </AntButton>
                            <AntButton
                                size="large"
                                onClick={resetProcess}
                            >
                                Volver a Paso 1
                            </AntButton>
                        </Space>
                    </div>
                )}

                {(currentStep >= 2 && currentStep <= 4) && (
                    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                        <Spin 
                            tip={
                                currentStep === 2 ? uploadStatus :
                                currentStep === 3 ? pythonStatus :
                                currentStep === 4 ? databaseStatus :
                                'Procesando...'
                            } 
                            size="large" 
                        />
                    </div>
                )}

                {currentStep === 5 && (
                    <div className="database-report">
                        <h3>{getReportTitle()}</h3>
                        {reportMetrics ? (
                            <>
                                <p>● Productos antes de la actualización: <strong>{reportMetrics.Productos_antes_de_la_actualización}</strong></p>
                                <p>● Productos agregados: <strong>{reportMetrics.Productos_agregados}</strong></p>
                                <p>● Precios actualizados: <strong>{reportMetrics.Precios_actualizados}</strong></p>
                                <p>● Productos luego de la actualización: <strong>{reportMetrics.Productos_luego_de_la_actualización}</strong></p>
                            </>
                        ) : (
                            <p>No se encontraron métricas del reporte.</p>
                        )}
                        <AntButton
                            type="primary"
                            size="large"
                            onClick={resetProcess}
                            style={{ marginTop: '20px' }}
                        >
                            Cargar otra lista
                        </AntButton>
                    </div>
                )}
            </div>
        </div>
        </ConfigProvider>
    );
};

export default RepoPage;